import { useState } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@components/ui/table";
import { Button } from "@components/ui/button";
import { ChevronDown, ChevronRight, ExternalLink } from "lucide-react";
import type { IntentoDetalle } from "@/hooks/analytics/useIntentosPorClienteFinal";
import { Skeleton } from "@components/ui/Skeleton";
import "./IntentosTable.css";

interface IntentosTableProps {
  intentos: IntentoDetalle[];
  isLoading?: boolean;
}

// Función simple para convertir Markdown básico a HTML
const markdownToHtml = (markdown: string): string => {
  let html = markdown;

  // Headers
  html = html.replace(/^### (.*$)/gim, "<h3>$1</h3>");
  html = html.replace(/^## (.*$)/gim, "<h2>$1</h2>");
  html = html.replace(/^# (.*$)/gim, "<h1>$1</h1>");

  // Bold
  html = html.replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>");
  html = html.replace(/__(.*?)__/gim, "<strong>$1</strong>");

  // Italic
  html = html.replace(/\*(.*?)\*/gim, "<em>$1</em>");
  html = html.replace(/_(.*?)_/gim, "<em>$1</em>");

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/gim, "<pre><code>$1</code></pre>");
  html = html.replace(/`(.*?)`/gim, "<code>$1</code>");

  // Links - asegurar que los links también respeten el ancho
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/gim, '<a href="$2" target="_blank" rel="noopener noreferrer" style="overflow-wrap: anywhere; word-break: break-word;">$1</a>');

  // Line breaks
  html = html.replace(/\n\n/gim, "</p><p>");
  html = html.replace(/\n/gim, "<br />");

  // Wrap in paragraph if not already wrapped
  if (!html.startsWith("<")) {
    html = `<p>${html}</p>`;
  }

  return html;
};

export function IntentosTable({ intentos, isLoading }: IntentosTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (intentoId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(intentoId)) {
      newExpanded.delete(intentoId);
    } else {
      newExpanded.add(intentoId);
    }
    setExpandedRows(newExpanded);
  };

  const getArchivoUrl = (archivo: any): string | null => {
    if (typeof archivo === "object" && archivo !== null) {
      if (archivo.url) return archivo.url;
      if (archivo.link) return archivo.link;
      if (archivo.path) return archivo.path;
    }
    if (typeof archivo === "string") {
      return archivo;
    }
    return null;
  };

  const formatRetroalimentacion = (retro: any): string => {
    if (typeof retro === "string") {
      return retro;
    }
    if (typeof retro === "object" && retro !== null) {
      if (retro.texto) return retro.texto;
      if (retro.feedback) return retro.feedback;
      if (retro.mensaje) return retro.mensaje;
      return JSON.stringify(retro, null, 2);
    }
    return "Sin retroalimentación disponible";
  };

  const formatFecha = (fecha: string) => {
    return new Date(fecha).toLocaleDateString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </div>
    );
  }

  if (intentos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No hay intentos registrados para este cliente final.
      </div>
    );
  }

  return (
    <div className="rounded-md border w-full overflow-hidden" style={{ maxWidth: "100%", boxSizing: "border-box" }}>
      <div className="w-full overflow-hidden" style={{ maxWidth: "100%" }}>
        <Table className="w-full table-fixed" style={{ maxWidth: "100%", tableLayout: "fixed", width: "100%" }}>
          <TableHeader className="intentos-table-header">
            <TableRow>
              <TableHead className="!whitespace-normal" style={{ width: "48px", minWidth: "48px", maxWidth: "48px", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}></TableHead>
              <TableHead className="!whitespace-normal" style={{ width: "12%", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}>Fecha</TableHead>
              <TableHead className="!whitespace-normal" style={{ width: "25%", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}>Aprendiz</TableHead>
              <TableHead className="!whitespace-normal" style={{ width: "22%", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}>Programa</TableHead>
              <TableHead className="!whitespace-normal" style={{ width: "22%", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}>Actividad</TableHead>
              <TableHead className="!whitespace-normal" style={{ width: "15%", backgroundColor: "hsl(var(--muted))", color: "hsl(var(--foreground))", fontWeight: 600 }}>Archivo</TableHead>
            </TableRow>
          </TableHeader>
        <TableBody>
          {intentos.map((intento) => {
            const isExpanded = expandedRows.has(intento.id);
            const archivoUrl = getArchivoUrl(intento.archivo);
            const retroTexto = formatRetroalimentacion(intento.retroalimentacion);
            const retroHtml = markdownToHtml(retroTexto);
            const nombreCompleto = `${intento.aprendiz.nombre} ${intento.aprendiz.apellidoPaterno}${intento.aprendiz.apellidoMaterno ? ` ${intento.aprendiz.apellidoMaterno}` : ""}`;

            return (
              <>
                <TableRow key={intento.id}>
                  <TableCell>
                    <Button
                      variant="icon"
                      size="sm"
                      onClick={() => toggleRow(intento.id)}
                      className="h-8 w-8 p-0"
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="!whitespace-normal">{formatFecha(intento.creadoEl)}</TableCell>
                  <TableCell className="!whitespace-normal">
                    <div>
                      <div className="font-medium truncate" title={nombreCompleto}>
                        {nombreCompleto}
                      </div>
                      <div className="text-sm text-muted-foreground truncate" title={intento.aprendiz.correo}>
                        {intento.aprendiz.correo}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="!whitespace-normal truncate" title={intento.actividad.programa.nombre}>
                    {intento.actividad.programa.nombre}
                  </TableCell>
                  <TableCell className="!whitespace-normal truncate" title={intento.actividad.nombre}>
                    {intento.actividad.nombre}
                  </TableCell>
                  <TableCell>
                    {archivoUrl ? (
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => window.open(archivoUrl, "_blank")}
                        className="h-auto p-0"
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Ver archivo
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
                {isExpanded && (
                  <TableRow>
                    <TableCell colSpan={6} className="bg-muted/50 p-4" style={{ maxWidth: 0, width: "100%" }}>
                      <div className="space-y-4" style={{ width: "100%", maxWidth: "100%" }}>
                        <div style={{ width: "100%", maxWidth: "100%" }}>
                          <h4 className="font-semibold mb-2">
                            Retroalimentación Completa
                          </h4>
                          <div
                            className="bg-background p-4 rounded-md border markdown-content"
                            style={{
                              lineHeight: "1.6",
                              overflowWrap: "anywhere",
                              wordWrap: "break-word",
                              wordBreak: "break-word",
                              overflowX: "visible",
                              maxWidth: "100%",
                              width: "100%",
                              boxSizing: "border-box",
                              whiteSpace: "normal",
                            }}
                            dangerouslySetInnerHTML={{ __html: retroHtml }}
                          />
                        </div>
                        {archivoUrl && (
                          <div>
                            <h4 className="font-semibold mb-2">Archivo</h4>
                            <Button
                              variant="outline"
                              onClick={() => window.open(archivoUrl, "_blank")}
                            >
                              <ExternalLink className="h-4 w-4 mr-2" />
                              Abrir archivo en nueva pestaña
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </>
            );
          })}
        </TableBody>
        </Table>
      </div>
    </div>
  );
}
